import * as React from "react";
import * as SwitchPrimitives from "@radix-ui/react-switch";

import { cn } from "@/lib/utils";

type SwitchSize = "sm" | "default" | "comfortable";

export type SwitchProps = React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> & {
  /**
   * `sm` — dense tables (admin). `comfortable` kept for callers; behaves like `default`.
   */
  size?: SwitchSize;
};

/** `block` + min/max width: Radix thumb is absolute-only; flex sizing can squash width→circle when layout is tight */
const switchTrack: Record<SwitchSize, string> = {
  sm: "h-[14px] min-h-[14px] max-h-[14px] w-[28px] min-w-[28px] max-w-[28px]",
  default: "h-4 min-h-4 max-h-4 w-8 min-w-8 max-w-8",
  comfortable: "h-4 min-h-4 max-h-4 w-8 min-w-8 max-w-8",
};

const switchThumb: Record<SwitchSize, string> = {
  sm: "top-1/2 h-[10px] w-[10px] -translate-y-1/2 left-[2px] data-[state=checked]:left-4",
  default: "top-1/2 h-3 w-3 -translate-y-1/2 left-[2px] data-[state=checked]:left-[18px]",
  comfortable: "top-1/2 h-3 w-3 -translate-y-1/2 left-[2px] data-[state=checked]:left-[18px]",
};

const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitives.Root>, SwitchProps>(
  ({ className, size = "default", ...props }, ref) => (
    <SwitchPrimitives.Root
      className={cn(
        "peer relative box-border shrink-0 cursor-pointer overflow-hidden rounded-full border border-border/40 transition-colors",
        "data-[state=checked]:bg-primary data-[state=unchecked]:bg-muted/80",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
        "disabled:cursor-not-allowed disabled:opacity-50",
        "block",
        switchTrack[size],
        className,
      )}
      {...props}
      ref={ref}
    >
      <SwitchPrimitives.Thumb
        className={cn(
          "pointer-events-none absolute block rounded-full bg-card shadow-sm ring-1 ring-border/30",
          "transition-[left] duration-200 ease-out",
          switchThumb[size],
        )}
      />
    </SwitchPrimitives.Root>
  ),
);
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
